import { useState, useEffect, useCallback, useRef } from 'react'
import { Box, Flex, Text, VStack, useT } from '@minetrace/ui'
import { useNavigate, useParams } from 'react-router-dom'
import { useFormColors, WizardSidebar } from '@minetrace/ui/forms'
import { toaster } from '@minetrace/ui'
import { proveedoresService } from '../../api/services'
import { ApiError } from '../../api/client'
import type { ProveedorResponse, MiningRightResponse, BankAccountResponse, FileAttachmentResponse, TaxReportResponse, PowerValidityResponse, ObservationResponse } from '../../api/models/response/proveedor'
import type { CreateMiningRightRequest } from '../../api/models/request/proveedor'
import type { AddCompanyPersonRequest } from '../../api/models/request/company'
import type { CompanyPersonResponse } from '../../api/models/response'
import type { RecpoRecordResponse, SinCapacidadOperativaResponse } from '../../api/models/response/consultarRuc'
import {
  LuFingerprint as Fingerprint,
  LuShieldCheck as ShieldCheck,
  LuFilePen as FileSignature,
  LuMountain as Mountain,
  LuFileText as FileText,
  LuFactory as Factory,
  LuPackage as Package,
  LuTriangleAlert as AlertTriangle,
  LuLandmark as Landmark,
  LuClipboardCheck as ClipboardCheck,
  LuUsers as Users,
} from 'react-icons/lu'

// Step components
import Step0Consent from './steps/Step0Consent'
import Step1Identity from './steps/Step1Identity'
import PowerValiditiesSection from './PowerValiditiesSection'
import Step2Accreditation from './steps/Step2Accreditation'
import Step3MiningRights from './steps/Step3MiningRights'
import Step4Igafom from './steps/Step4Igafom'
import Step4bLegalDocuments from './steps/Step4bLegalDocuments'
import Step5Production from './steps/Step5Production'
import Step6Recpo from './steps/Step6Recpo'
import Step7Iqbf from './steps/Step7Iqbf'
import Step2bContrato from './steps/Step2bContrato'
import Step8Finance from './steps/Step8Finance'
import Step10Ubo from './steps/Step10Ubo'
import Step9Verification from './steps/Step9Verification'
import ObservationReplies from '../../components/ObservationReplies'
import type { AddShareholderRequest } from '../../api/models/request/proveedor'
import { TAB_TO_APPROVAL_STEP } from '../../lib/supplierApprovalSteps'

// ── Types ──
type TabType = 'consentimiento' | 'identidad' | 'acreditacion' | 'contrato' | 'derechos' | 'igafom' | 'legaldocs' | 'produccion' | 'recpo' | 'iqbf' | 'finanzas' | 'accionistas' | 'verificaciones'

// El tab 'accionistas' (UBO) solo aplica a sociedades S.A./S.A.C.; se filtra del
// orden cuando el proveedor no es sociedad (ver TAB_ORDER abajo).
const TAB_ORDER_REGISTER: TabType[] = ['identidad', 'acreditacion', 'contrato', 'derechos', 'igafom', 'legaldocs', 'produccion', 'recpo', 'iqbf', 'finanzas', 'accionistas', 'consentimiento']
const TAB_ORDER_EDIT: TabType[] = ['identidad', 'acreditacion', 'contrato', 'derechos', 'igafom', 'legaldocs', 'produccion', 'recpo', 'iqbf', 'finanzas', 'accionistas', 'verificaciones']

// Backend's RegistrationStep enum (1..11). Step 10 es "Accionistas" (solo
// sociedades) y step 11 es Verificaciones (edición post-aprobación) o el paso de
// consentimiento durante el registro (el backend reusa el último valor).
const STEP_TO_TAB_REGISTER: Record<number, TabType> = {
  1: 'identidad', 2: 'acreditacion', 3: 'contrato', 4: 'derechos', 5: 'igafom',
  6: 'produccion', 7: 'recpo', 8: 'iqbf', 9: 'finanzas', 10: 'accionistas', 11: 'consentimiento',
}
const STEP_TO_TAB_EDIT: Record<number, TabType> = {
  1: 'identidad', 2: 'acreditacion', 3: 'contrato', 4: 'derechos', 5: 'igafom',
  6: 'produccion', 7: 'recpo', 8: 'iqbf', 9: 'finanzas', 10: 'accionistas', 11: 'verificaciones',
}

// Inverso (tab → paso del backend) para filtrar las observaciones del evaluador por
// pestaña. legaldocs comparte el paso 5 con igafom; las observaciones de ese paso se
// muestran en ambas. Igual que el mapeo del evaluador (EvaluarProveedor).
// Mismo mapa en registro y evaluación (ver lib/supplierApprovalSteps).
const TAB_TO_STEP: Record<TabType, number> = TAB_TO_APPROVAL_STEP

// Tipo de contribuyente SUNAT que habilita el tab de Accionistas (UBO).
function isCorporationType(taxpayerType?: string | null): boolean {
  if (!taxpayerType) return false
  const t = taxpayerType.toUpperCase()
  if (t.includes('SOCIEDAD ANONIMA') || t.includes('SOCIEDAD ANÓNIMA')) return true
  const compact = t.replace(/[.\s]/g, '')
  return compact.includes('SAC') || compact.includes('SAA')
}

// Steps that have forms managed via ref (trigger/getValues)
const FORM_STEPS: Set<TabType> = new Set(['identidad', 'acreditacion', 'finanzas'])


// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StepFormRef = { trigger: () => Promise<boolean>; getValues: () => any } | null

export default function NuevoProveedorPage() {
  const navigate = useNavigate()
  const c = useFormColors()
  const { t } = useT()
  const { id } = useParams()
  const isEditing = Boolean(id)
  const [loadedRegStatus, setLoadedRegStatus] = useState(0) // 0=Draft, 1=PendingReview, 2=Observed, 3=Approved
  const isFullyRegistered = loadedRegStatus === 3 // Approved

  // Un minero aprobado se cambia SÓLO con una solicitud de modificación: el backend rechaza
  // la edición directa (409). Antes esta pantalla dejaba editarlo todo y fallaba recién al
  // guardar; ahora lleva a sus solicitudes.
  useEffect(() => {
    if (!isFullyRegistered || !id) return
    toaster.info({ description: 'Este minero ya está aprobado: sus datos se cambian con una solicitud de modificación.' })
    navigate(`/app/proveedores/${id}/modificaciones`, { replace: true })
  }, [isFullyRegistered, id, navigate])

  // Wizard navigation
  const [activeTab, setActiveTab] = useState<TabType>('identidad')
  const [loadingCompany, setLoadingCompany] = useState(isEditing)
  const [completedSteps, setCompletedSteps] = useState<Set<TabType>>(new Set())
  const [companyId, setCompanyId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [proveedor, setProveedor] = useState<ProveedorResponse | null>(null)
  // Observaciones del evaluador (estilo PR): el registrador las ve por pestaña y puede responder.
  const [observations, setObservations] = useState<ObservationResponse[]>([])

  // Single ref for the active step's form methods (trigger, getValues)
  const activeStepRef = useRef<StepFormRef>(null)

  // Step 1: Contactos + consulta result cache
  const [persons, setPersons] = useState<CompanyPersonResponse[]>([])
  const [pendingContacts, setPendingContacts] = useState<Map<string, AddCompanyPersonRequest>>(new Map())
  const [consultaData, setConsultaData] = useState<{
    economicActivities: string[]; taxpayerStatus: string | null
    taxpayerCondition: string | null; taxpayerType: string | null
    activityStartDate: string | null
  }>({ economicActivities: [], taxpayerStatus: null, taxpayerCondition: null, taxpayerType: null, activityStartDate: null })

  // El tab de Accionistas (UBO) solo aplica a sociedades S.A./S.A.C. Tras la
  // consulta de RUC usamos tipoContribuyente; con el proveedor cargado, su flag.
  const isCorporation = proveedor?.isCorporation ?? isCorporationType(consultaData.taxpayerType)
  const baseOrder = isFullyRegistered ? TAB_ORDER_EDIT : TAB_ORDER_REGISTER
  const TAB_ORDER = isCorporation ? baseOrder : baseOrder.filter(tab => tab !== 'accionistas')

  // Step 1 default values for form initialization
  const [step1Defaults, setStep1Defaults] = useState<Record<string, unknown>>({})

  // Step 2: Acreditacion state
  const [minerSource, setMinerSource] = useState<string | null>(null)
  const [reinfoSyncedAt, setReinfoSyncedAt] = useState<string | null>(null)
  const [sourceTimestamps, setSourceTimestamps] = useState<import('./steps/Step1Identity').Step1ConsultaResult['sourceTimestamps']>({})
  // Verificación SUNAT "Sin Capacidad Operativa" (SSCO) capturada en la consulta de RUC
  const [sinCapacidadOperativa, setSinCapacidadOperativa] = useState<SinCapacidadOperativaResponse | null>(null)

  // Step 3: Derechos Mineros state
  const [miningRights, setMiningRights] = useState<CreateMiningRightRequest[]>([])
  const [savedMiningRights, setSavedMiningRights] = useState<MiningRightResponse[]>([])

  // Step 6: RECPO
  const [recpoRecords, setRecpoRecords] = useState<RecpoRecordResponse[]>([])

  // Step 8: Finanzas state
  const [bankAccounts, setBankAccounts] = useState<BankAccountResponse[]>([])
  const [splaftDeclared, setSplaftDeclared] = useState(false)
  const [incomeTaxCategoryId, setIncomeTaxCategoryId] = useState(0)
  const [splaftFile, setSplaftFile] = useState<File | null>(null)
  const [splaftDocumentFile, setSplaftDocumentFile] = useState<FileAttachmentResponse | null>(null)
  const [taxReports, setTaxReports] = useState<TaxReportResponse[]>([])
  const [powerValidities, setPowerValidities] = useState<PowerValidityResponse[]>([])
  // Power validities the user staged in step 1 before clicking Siguiente.
  // They travel with the saveStepIdentity request and are persisted server-
  // side once the Company + legal-rep CompanyPerson exist.
  const [pendingPowerValidities, setPendingPowerValidities] = useState<
    Array<{ localId: string; code: string; issueDate: string; file: File }>
  >([])

  // Step 0: Consentimiento
  const [consentFile, setConsentFile] = useState<File | null>(null)
  const [consentError, setConsentError] = useState<{ code: string; message: string } | null>(null)

  // ── Populate from response ──
  const populateFromResponse = (co: ProveedorResponse) => {
    setStep1Defaults({
      ruc: co.ruc, legalName: co.legalName, commercialName: co.commercialName ?? '', economicActivity: co.economicActivity ?? 0,
      taxAddress: co.taxAddress ?? '', ubigeoCode: co.ubigeoCode ?? '', mainPhone: co.mainPhone ?? '', mainEmail: co.mainEmail ?? '',
      website: co.website ?? '', prefix: co.prefix ?? '',
    })
    setConsultaData({
      economicActivities: co.economicActivities ?? [],
      taxpayerStatus: co.taxpayerStatus ?? null,
      taxpayerCondition: co.taxpayerCondition ?? null,
      taxpayerType: co.taxpayerType ?? null,
      activityStartDate: co.activityStartDate ?? null,
    })
    // Rehidrata la verificación SSCO persistida para mostrarla siempre al reabrir.
    const ms = co.miningSupplier
    setSinCapacidadOperativa(ms?.sinCapacidadOperativaCheckedAt ? {
      found: ms.sinCapacidadOperativaFound ?? false,
      sourceAvailable: true,
      checkedAt: ms.sinCapacidadOperativaCheckedAt,
      padronDate: ms.sinCapacidadOperativaPadronDate ?? null,
    } : null)
    if (co.miningSupplier?.categoryId) setStep2CategoryId(co.miningSupplier.categoryId)
    if (co.miningSupplier?.incomeTaxCategoryId) setIncomeTaxCategoryId(co.miningSupplier.incomeTaxCategoryId)
    if (co.persons) setPersons(co.persons)
    if (co.bankAccounts) setBankAccounts(co.bankAccounts)
    if (co.miningRights) {
      setSavedMiningRights(co.miningRights)
      setMiningRights(co.miningRights.map(mr => ({ uniqueCode: mr.uniqueCode, name: mr.name, ubigeoCode: mr.ubigeoCode ?? undefined, miningRightStatusId: mr.miningRightStatusId })))
    }
    if (co.recpoRecords) setRecpoRecords(co.recpoRecords)
    // minerSource from the persisted MiningSupplier entity
    if (co.miningSupplier?.minerSource) setMinerSource(co.miningSupplier.minerSource)
    else if (co.legalCondition === 1) setMinerSource('FormalizedMiner')
    else if (co.legalCondition === 2) setMinerSource('Reinfo')
    if (co.reinfoLastUpdate) setReinfoSyncedAt(co.reinfoLastUpdate)
    setSplaftDeclared(co.splaftDeclared ?? false)
    if (co.splaftDocumentFile) setSplaftDocumentFile(co.splaftDocumentFile)
    if (co.taxReports) setTaxReports(co.taxReports)
    if (co.powerValidities) setPowerValidities(co.powerValidities)
  }

  // Step 2 default category (for passing to Step2Accreditation)
  const [step2CategoryId, setStep2CategoryId] = useState(0)

  // ── Load proveedor ──
  useEffect(() => {
    if (!id) return
    const loadProveedor = async () => {
      setLoadingCompany(true)
      try {
        const data = await proveedoresService.getById(id)
        setLoadedRegStatus(data.registrationStatus)
        setProveedor(data)
        setObservations(data.observations ?? [])
        populateFromResponse(data)
        const isApproved = data.registrationStatus === 3
        const order = isApproved ? TAB_ORDER_EDIT : TAB_ORDER_REGISTER
        if (data.registrationStatus >= 1) {
          // PendingReview or beyond = all steps completed
          setCompletedSteps(new Set(order))
        } else if (data.wizardCompletedAt != null) {
          // Draft pero ya pasó por todos los steps (esperando "Enviar a
          // Evaluación"). Marcamos todo completo y abrimos el último paso.
          setCompletedSteps(new Set(order))
          setActiveTab(order[order.length - 1]!)
        } else if (data.currentStep) {
          const stepToTab = isApproved ? STEP_TO_TAB_EDIT : STEP_TO_TAB_REGISTER
          const tab = stepToTab[data.currentStep]
          const idx = order.indexOf(tab ?? 'identidad')
          setCompletedSteps(new Set(order.slice(0, idx)))
          if (tab) setActiveTab(tab)
        }
      } catch { /* silently fail */ } finally { setLoadingCompany(false) }
    }
    loadProveedor()
  }, [id])

  // Handle Step1 consulta result
  const handleConsultaCompleted = useCallback((result: import('./steps/Step1Identity').Step1ConsultaResult) => {
    setConsultaData({
      economicActivities: result.economicActivities,
      taxpayerStatus: result.taxpayerStatus,
      taxpayerCondition: result.taxpayerCondition,
      taxpayerType: result.taxpayerType,
      activityStartDate: result.activityStartDate,
    })
    // Mirror the consulted RUC into step1Defaults so PJ-only sections
    // (e.g. PowerValiditiesSection, gated on RUC starting with "20") become
    // visible during step 1 entry, before the company has been saved.
    setStep1Defaults(prev => ({ ...prev, ruc: result.ruc }))
    setMiningRights(result.miningRights)
    setRecpoRecords(result.recpoRecords)
    setMinerSource(result.minerSource)
    setReinfoSyncedAt(result.reinfoSyncedAt)
    setSourceTimestamps(result.sourceTimestamps)
    setSinCapacidadOperativa(result.sinCapacidadOperativa)
  }, [])

  // ── Mining rights handlers ──
  const refreshMiningRights = async () => {
    const cid = companyId ?? id
    if (!cid) return
    try { const data = await proveedoresService.getById(cid); if (data.miningRights) setSavedMiningRights(data.miningRights) } catch { /* */ }
  }

  // Re-fetch the full proveedor (used by the Accionistas/UBO step to refresh
  // shareholders + their validation status after each add/update/remove).
  const refreshProveedor = async () => {
    const cid = companyId ?? id
    if (!cid) return
    try { const data = await proveedoresService.getById(cid); setProveedor(data) } catch { /* */ }
  }

  // ── Contrato handler ──
  const handleUploadContract = async (file: File, contractDate?: string, notes?: string, expirationDate?: string) => {
    const cid = companyId ?? id
    if (!cid) return
    await proveedoresService.uploadContract(cid, file, contractDate, notes, expirationDate)
    try { const data = await proveedoresService.getById(cid); setProveedor(data) } catch { /* */ }
  }

  const handleSavePolygon = async (miningRightId: string, vertices: { northing: number; easting: number }[], utmZone: number) => {
    const cid = companyId ?? id; if (!cid) return
    await proveedoresService.updatePolygon(cid, miningRightId, { vertices, utmZone }); await refreshMiningRights()
  }
  const handleAddComponent = async (miningRightId: string, data: { componentTypeId: string; name?: string; northing: number; easting: number }) => {
    const cid = companyId ?? id; if (!cid) throw new Error('No company id')
    const result = await proveedoresService.addComponent(cid, miningRightId, data); await refreshMiningRights(); return result
  }
  const handleRemoveComponent = async (miningRightId: string, componentId: string) => {
    const cid = companyId ?? id; if (!cid) return
    await proveedoresService.removeComponent(cid, miningRightId, componentId); await refreshMiningRights()
  }

  // ── Wizard step handler ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleWizardStep = async (tab: TabType, values?: any) => {
    setIsSaving(true)
    try {
      const cid = companyId ?? id

      if (tab === 'consentimiento') {
        if (!cid) { toaster.error({ description: t('proveedores.wizard.toasts.supplierNotFound') }); return }
        const hasExistingConsent = proveedor?.consentDocumentFile != null
        if (!consentFile && !hasExistingConsent) {
          toaster.error({ description: t('proveedores.wizard.toasts.consentRequired') })
          return
        }
        setConsentError(null)
        // Only re-upload when the user picked a new file. If they're just
        // saving with the previously-accepted consent untouched, skip the
        // validator round-trip.
        if (consentFile) {
          try {
            await proveedoresService.saveStepConsent(cid, consentFile)
          } catch (e) {
            const code = e instanceof ApiError ? e.errorCode : null
            const message = e instanceof Error ? e.message : 'No se pudo validar el consentimiento'
            if (code === 'CONSENT_QR_MISMATCH' || code === 'CONSENT_TEMPLATE_VERSION_CHANGED' || code === 'CONSENT_FILE_NOT_PDF') {
              setConsentError({ code, message })
            }
            // apiClient ya muestra un toast genérico; aquí solo levantamos el panel inline
            // para los códigos conocidos. Evita la doble alerta del lado del componente.
            return
          }
        }
        setCompletedSteps(prev => new Set(prev).add('consentimiento'))
        if (!isFullyRegistered && proveedor?.wizardCompletedAt == null) {
          await proveedoresService.saveStepFinalize(cid)
          toaster.success({ description: t('proveedores.wizard.toasts.draftSaved') })
          setTimeout(() => navigate('/app/proveedores'), 1500)
        } else {
          navigate('/app/proveedores')
        }
        return
      }

      if (tab === 'identidad') {
        const stagedPvFiles = !cid ? pendingPowerValidities.map(p => p.file) : []
        const result = await proveedoresService.saveStepIdentity({
          id: cid ?? undefined,
          ruc: values?.ruc,
          legalName: values?.legalName,
          commercialName: values?.commercialName || undefined,
          economicActivity: values?.economicActivity || undefined,
          economicActivities: consultaData.economicActivities.length > 0 ? consultaData.economicActivities : undefined,
          taxpayerStatus: consultaData.taxpayerStatus || undefined,
          taxpayerCondition: consultaData.taxpayerCondition || undefined,
          taxpayerType: consultaData.taxpayerType || undefined,
          taxAddress: values?.taxAddress || undefined,
          ubigeoCode: values?.ubigeoCode || undefined,
          mainPhone: values?.mainPhone || undefined,
          mainEmail: values?.mainEmail || undefined,
          website: values?.website || undefined,
          prefix: values?.prefix || undefined,
          sunatVerified: consultaData.taxpayerStatus ? true : undefined,
          minerSource: minerSource || undefined,
          reinfoSyncedAt: reinfoSyncedAt || undefined,
          ...sourceTimestamps,
          activityStartDate: consultaData.activityStartDate || undefined,
          sinCapacidadOperativaCheckedAt: sinCapacidadOperativa?.checkedAt || undefined,
          sinCapacidadOperativaFound: sinCapacidadOperativa ? sinCapacidadOperativa.found : undefined,
          sinCapacidadOperativaPadronDate: sinCapacidadOperativa?.padronDate || undefined,
          miningRights: miningRights.length > 0 ? miningRights : undefined,
          contacts: !cid && pendingContacts.size > 0 ? Array.from(pendingContacts.values()) : undefined,
          // FileKey de cada vigencia debe matchear el campo del FormData
          // ("pv_file_0", "pv_file_1", …) — el service los appendea en el
          // mismo orden que reciben aquí.
          pendingPowerValidities: stagedPvFiles.length > 0
            ? pendingPowerValidities.map((p, idx) => ({
                code: p.code,
                issueDate: p.issueDate,
                fileKey: `pv_file_${idx}`,
              }))
            : undefined,
        }, stagedPvFiles.length > 0 ? stagedPvFiles : undefined)
        if (!result) return
        setCompanyId(result.id)
        // La respuesta del guardado es PARCIAL (en la edición no trae ubigeo, verificación
        // SSCO, contactos, vigencias, consentimiento…). Usarla como estado completo vaciaba
        // esos campos y el siguiente "Siguiente" los borraba en la base. Se recarga el
        // minero; si la lectura falla se cae a la respuesta del guardado como antes.
        let full = result
        try { full = await proveedoresService.getById(result.id) } catch { /* fallback */ }
        // Los sellos ya quedaron guardados: no re-enviarlos en cada guardado posterior.
        setSourceTimestamps({})
        // Hydrate local snapshots from the save response so sections that key
        // off step1Defaults / proveedor (e.g. PowerValiditiesSection, which
        // gates on RUC starting with "20") render correctly without waiting
        // for — or relying on — the post-navigate re-fetch. The catch in
        // loadProveedor swallows tenant-scope failures (e.g. host-tenant
        // superadmin reading a freshly-created Company before its TenantId
        // is in scope), and that stale empty step1Defaults was hiding the
        // Vigencia de Poder section.
        setProveedor(full)
        populateFromResponse(full)
        if (full.miningRights) setSavedMiningRights(full.miningRights)
        // Las staged power validities ya fueron persistidas por el backend
        // y vienen en result.powerValidities (poblado por populateFromResponse).
        // Limpiamos el buffer local para no duplicarlas en la tabla.
        if (pendingPowerValidities.length > 0) setPendingPowerValidities([])
        // Replace URL so F5 reloads the existing record, not a blank "nuevo"
        if (!id) navigate(`/app/proveedores/${result.id}`, { replace: true })
        setCompletedSteps(prev => new Set(prev).add('identidad'))
        setActiveTab('acreditacion')

      } else if (tab === 'acreditacion') {
        if (!cid) { toaster.error({ description: t('proveedores.wizard.toasts.supplierNotFound') }); return }
        const derivedLegalCondition = minerSource === 'FormalizedMiner' ? 1 : minerSource === 'Reinfo' ? 2 : undefined
        const categoryId = values?.categoryId || undefined
        // La fecha de inicio de actividades viene de la consulta a SUNAT. El campo ya
        // existia en la entidad y el detalle lo mostraba, pero nadie lo llenaba: no hay
        // input y la consulta lo descartaba.
        await proveedoresService.saveStepAccreditation(cid, {
          legalCondition: derivedLegalCondition,
          categoryId,
          activityStartDate: consultaData.activityStartDate ?? undefined,
        })
        setCompletedSteps(prev => new Set(prev).add('acreditacion'))
        setActiveTab('contrato')

      } else if (tab === 'contrato') {
        // El documento del contrato se carga vía uploadContract (opcional);
        // "Siguiente" solo marca el avance del paso y pasa a derechos.
        if (cid) await proveedoresService.saveStepContract(cid)
        setCompletedSteps(prev => new Set(prev).add('contrato'))
        setActiveTab('derechos')

      } else if (tab === 'derechos') {
        if (!cid) { toaster.error({ description: t('proveedores.wizard.toasts.supplierNotFound') }); return }
        await proveedoresService.saveStepMiningRights(cid, miningRights)
        // Re-leer con GetById para preservar las navegaciones (igafomRecords,
        // legalDocumentRecords, productionDeclarations, components). El response
        // de saveStepMiningRights solo devuelve los DMs sin esas navegaciones,
        // y sobreescribir el state con eso pierde los archivos ya cargados.
        await refreshMiningRights()
        setCompletedSteps(prev => new Set(prev).add('derechos'))
        setActiveTab('igafom')

      } else if (tab === 'igafom') {
        if (cid) await proveedoresService.saveStepIgafom(cid)
        setCompletedSteps(prev => new Set(prev).add('igafom'))
        setActiveTab('legaldocs')

      } else if (tab === 'legaldocs') {
        // No hay endpoint dedicado para "Otros Documentos Legales": los
        // registros se gestionan vía add/update/remove por DM. "Siguiente"
        // solo marca el paso como completado y avanza a produccion.
        setCompletedSteps(prev => new Set(prev).add('legaldocs'))
        setActiveTab('produccion')

      } else if (tab === 'produccion') {
        if (cid) await proveedoresService.saveStepProduction(cid)
        setCompletedSteps(prev => new Set(prev).add('produccion'))
        setActiveTab('recpo')

      } else if (tab === 'recpo') {
        if (cid) await proveedoresService.saveStepRecpo(cid)
        setCompletedSteps(prev => new Set(prev).add('recpo'))
        setActiveTab('iqbf')

      } else if (tab === 'iqbf') {
        // Los datos (switch, autorizaciones y manipuladores) se guardan por D.M.
        // en sus propios endpoints; aquí sólo se marca el avance del paso.
        if (cid) await proveedoresService.saveStepIqbf(cid)
        setCompletedSteps(prev => new Set(prev).add('iqbf'))
        setActiveTab('finanzas')

      } else if (tab === 'finanzas') {
        if (!cid) { toaster.error({ description: t('proveedores.wizard.toasts.supplierNotFound') }); return }
        await proveedoresService.saveStepFinance(cid, {
          splaftDeclared: values?.splaftDeclared ?? false,
          incomeTaxCategoryId: incomeTaxCategoryId || undefined,
        }, splaftFile ?? undefined)
        // Ya subido: sin esto cada "Siguiente" volvía a subir el mismo PDF (adjuntos huérfanos).
        // Se trae el adjunto guardado para que, al volver al paso, no se exija subirlo otra vez.
        if (splaftFile) {
          setSplaftFile(null)
          try {
            const fresh = await proveedoresService.getById(cid)
            setProveedor(fresh)
            if (fresh.splaftDocumentFile) setSplaftDocumentFile(fresh.splaftDocumentFile)
          } catch { /* el siguiente load lo trae */ }
        }
        setCompletedSteps(prev => new Set(prev).add('finanzas'))
        // Las sociedades pasan por el paso de Accionistas (UBO) antes del cierre.
        if (isCorporation) {
          setActiveTab('accionistas')
        } else if (!isFullyRegistered) {
          // Consentimiento is now the last registration step. Finalize +
          // submission happen there once the signed QR document is validated.
          setActiveTab('consentimiento')
        } else {
          setActiveTab('verificaciones')
        }

      } else if (tab === 'accionistas') {
        if (cid) await proveedoresService.saveStepShareholders(cid)
        setCompletedSteps(prev => new Set(prev).add('accionistas'))
        if (!isFullyRegistered) setActiveTab('consentimiento')
        else setActiveTab('verificaciones')

      } else if (tab === 'verificaciones') {
        // Only reachable in edit mode (Approved) — just save and exit
        navigate('/app/proveedores')
      }
    } catch {
      // Error already handled by apiClient toast
    } finally { setIsSaving(false) }
  }

  // ── Navigation ──
  const handleTabChange = (tab: TabType) => {
    if (isFullyRegistered) { setActiveTab(tab); return }
    const tabIndex = TAB_ORDER.indexOf(tab)
    const currentIndex = TAB_ORDER.indexOf(activeTab)
    if (tabIndex <= currentIndex) { setActiveTab(tab); return }
    if (completedSteps.has(TAB_ORDER[tabIndex - 1]!)) setActiveTab(tab)
  }

  const handleBack = () => {
    const idx = TAB_ORDER.indexOf(activeTab)
    if (idx > 0) setActiveTab(TAB_ORDER[idx - 1]!)
  }

  const handleSaveAndExit = () => { if (isEditing && id) navigate('/app/proveedores') }

  const handleStepSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // For steps with forms, validate via ref before advancing
    if (FORM_STEPS.has(activeTab) && activeStepRef.current?.trigger) {
      const isValid = await activeStepRef.current.trigger()
      if (!isValid) return
    }
    if (activeTab === 'identidad' && !minerSource) {
      toaster.error({ description: t('proveedores.wizard.toasts.rucNotAMiner') })
      return
    }
    const values = activeStepRef.current?.getValues?.()
    await handleWizardStep(activeTab, values)
  }

  // ── Tab config ──
  const tabs = [
    { key: 'identidad' as const, label: t('proveedores.wizard.tabs.identidad'), icon: <Fingerprint size={18} />, hasError: false },
    { key: 'acreditacion' as const, label: t('proveedores.wizard.tabs.acreditacion'), icon: <ShieldCheck size={18} />, hasError: false },
    { key: 'contrato' as const, label: t('proveedores.wizard.tabs.contrato'), icon: <FileSignature size={18} />, hasError: false },
    { key: 'derechos' as const, label: t('proveedores.wizard.tabs.derechos'), icon: <Mountain size={18} />, hasError: false },
    { key: 'igafom' as const, label: t('proveedores.wizard.tabs.igafom'), icon: <FileText size={18} />, hasError: false },
    { key: 'legaldocs' as const, label: t('proveedores.wizard.tabs.legaldocs'), icon: <FileText size={18} />, hasError: false },
    { key: 'produccion' as const, label: t('proveedores.wizard.tabs.produccion'), icon: <Factory size={18} />, hasError: false },
    { key: 'recpo' as const, label: t('proveedores.wizard.tabs.recpo'), icon: <Package size={18} />, hasError: false },
    { key: 'iqbf' as const, label: t('proveedores.wizard.tabs.iqbf'), icon: <AlertTriangle size={18} />, hasError: false },
    { key: 'finanzas' as const, label: t('proveedores.wizard.tabs.finanzas'), icon: <Landmark size={18} />, hasError: false },
    ...(isCorporation
      ? [{ key: 'accionistas' as const, label: t('proveedores.wizard.tabs.accionistas'), icon: <Users size={18} />, hasError: false }]
      : []),
    ...(isFullyRegistered
      ? [{ key: 'verificaciones' as const, label: t('proveedores.wizard.tabs.verificaciones'), icon: <ClipboardCheck size={18} />, hasError: false }]
      : [{ key: 'consentimiento' as const, label: t('proveedores.wizard.tabs.consentimiento'), icon: <FileSignature size={18} />, hasError: false }]),
  ]

  if (loadingCompany) {
    return (
      <Box p={8} textAlign="center"><Text tid="proveedores.wizard.page.loading" color={c.textMuted} /></Box>
    )
  }

  const effectiveCompanyId = companyId ?? id ?? null
  const activeIndex = TAB_ORDER.indexOf(activeTab)

  // Step title mapping
  const STEP_TITLES: Record<TabType, { title: string; description: string }> = {
    consentimiento: { title: t('proveedores.wizard.headers.consentTitle'), description: t('proveedores.wizard.headers.consentDescription') },
    identidad: { title: t('proveedores.wizard.headers.identidadTitle'), description: t('proveedores.wizard.headers.identidadDescription') },
    acreditacion: { title: t('proveedores.wizard.headers.acreditacionTitle'), description: t('proveedores.wizard.headers.acreditacionDescription') },
    contrato: { title: t('proveedores.wizard.headers.contratoTitle'), description: t('proveedores.wizard.headers.contratoDescription') },
    derechos: { title: t('proveedores.wizard.headers.derechosTitle'), description: t('proveedores.wizard.headers.derechosDescription') },
    igafom: { title: t('proveedores.wizard.headers.igafomTitle'), description: t('proveedores.wizard.headers.igafomDescription') },
    legaldocs: { title: t('proveedores.wizard.headers.legaldocsTitle'), description: t('proveedores.wizard.headers.legaldocsDescription') },
    produccion: { title: t('proveedores.wizard.headers.produccionTitle'), description: t('proveedores.wizard.headers.produccionDescription') },
    recpo: { title: t('proveedores.wizard.headers.recpoTitle'), description: t('proveedores.wizard.headers.recpoDescription') },
    iqbf: { title: t('proveedores.wizard.headers.iqbfTitle'), description: t('proveedores.wizard.headers.iqbfDescription') },
    finanzas: { title: t('proveedores.wizard.headers.finanzasTitle'), description: t('proveedores.wizard.headers.finanzasDescription') },
    accionistas: { title: t('proveedores.wizard.headers.accionistasTitle'), description: t('proveedores.wizard.headers.accionistasDescription') },
    verificaciones: { title: t('proveedores.wizard.headers.verificacionesTitle'), description: t('proveedores.wizard.headers.verificacionesDescription') },
  }

  const currentStep = STEP_TITLES[activeTab]

  const refreshObservations = async () => {
    const cid = companyId ?? id
    if (!cid) return
    try {
      const data = await proveedoresService.getById(cid)
      setObservations(data.observations ?? [])
    } catch { /* ignore */ }
  }

  const handleObsReply = async (obsId: string, text: string) => {
    const cid = companyId ?? id
    if (!cid) return
    try {
      await proveedoresService.addObservationReply(cid, obsId, text)
      await refreshObservations()
    } catch {
      toaster.error({ description: t('proveedores.wizard.observations.replyError') })
    }
  }

  // Observaciones abiertas del paso activo: banner ámbar con el texto del evaluador
  // y el hilo de respuestas (el registrador puede responder, estilo PR).
  const openObsForActiveTab = observations.filter(o => o.step === TAB_TO_STEP[activeTab] && !o.resolvedAt)
  const renderTabObservations = () => {
    if (openObsForActiveTab.length === 0) return null
    return (
      <Box mb={5} p={4} borderRadius="xl" bg="rgba(212, 154, 42, 0.08)" border="1px solid" borderColor="rgba(212, 154, 42, 0.35)">
        <Flex alignItems="center" gap={2} mb={3}>
          <AlertTriangle size={16} color="#8a5f0b" />
          <Text fontSize="sm" fontWeight="700" color="#8a5f0b">{t('proveedores.wizard.observations.tabTitle')}</Text>
        </Flex>
        <VStack gap={4} alignItems="stretch">
          {openObsForActiveTab.map(o => (
            <Box key={o.id}>
              <Text fontSize="sm" color={c.text} fontWeight="500">{o.observationText}</Text>
              <ObservationReplies
                replies={(o.replies ?? []).map(r => ({ id: r.id, replyText: r.replyText, createdByUserName: r.createdByUserName, createdAt: r.createdAt }))}
                canReply
                onReply={(text) => handleObsReply(o.id, text)}
              />
            </Box>
          ))}
        </VStack>
        <Text fontSize="xs" color={c.textMuted} mt={3}>{t('proveedores.wizard.observations.tabHint')}</Text>
      </Box>
    )
  }

  return (
    <Flex mx={-8} mt={-4}>
      {/* Sidebar */}
      <WizardSidebar
        title={isFullyRegistered ? t('proveedores.wizard.page.titleEdit') : t('proveedores.wizard.page.titleNew')}
        subtitle={t('proveedores.wizard.page.stepCounter', { current: activeIndex + 1, total: TAB_ORDER.length })}
        steps={tabs}
        activeStep={activeTab}
        completedSteps={completedSteps}
        onStepClick={handleTabChange}
      />

      {/* Main Content */}
      <Box flex={1} py={4} px={6} bg="white" display="flex" flexDirection="column" minH="calc(100vh - 2rem)">
        <Box flex={1} display="flex" flexDirection="column">
          {/* Step Header */}
          <Box mb={6}>
            {effectiveCompanyId && proveedor && (
              <Flex gap={3} alignItems="center" mb={2}>
                <Text fontSize="sm" fontWeight="700" color={c.text}>{proveedor.legalName}</Text>
                <Text tid="proveedores.wizard.page.rucBadge" values={{ ruc: proveedor.ruc }} fontSize="xs" color={c.textSecondary} fontFamily="monospace" bg="gray.100" px={2} py={0.5} borderRadius="md" />
              </Flex>
            )}
            <Text fontSize="3xl" fontWeight="800" color={c.accent} fontFamily="heading">{currentStep.title}</Text>
            <Text color={c.textSecondary} mt={2} maxW="2xl">{currentStep.description}</Text>
          </Box>

          {/* Form Content */}
          <form onSubmit={handleStepSubmit} noValidate style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {renderTabObservations()}
                {activeTab === 'consentimiento' && (
                  <Step0Consent
                    consentFile={consentFile}
                    onFileChange={(f) => { setConsentFile(f); setConsentError(null) }}
                    companyId={effectiveCompanyId}
                    ruc={proveedor?.ruc ?? ''}
                    error={consentError}
                    existingConsent={proveedor?.consentDocumentFile ?? null}
                    existingConsentAcceptedAt={proveedor?.consentAcceptedAt ?? null}
                  />
                )}

                {activeTab === 'identidad' && (
                  <>
                    <Step1Identity
                      ref={activeStepRef}
                      mode="edit"
                      defaultValues={step1Defaults}
                      onConsultaCompleted={handleConsultaCompleted}
                      initialConsultaData={{ ...consultaData, sinCapacidadOperativa }}
                      companyId={effectiveCompanyId} persons={persons} onPersonsChange={setPersons}
                      addPerson={proveedoresService.addPerson} removePerson={proveedoresService.removePerson}
                      onPendingContactAdd={(localId, req) => setPendingContacts(prev => new Map(prev).set(localId, req))}
                      onPendingContactRemove={(localId) => setPendingContacts(prev => { const next = new Map(prev); next.delete(localId); return next })}
                    />
                    {(((step1Defaults.ruc as string | null | undefined) ?? proveedor?.ruc) ?? '').startsWith('20') && (
                      <PowerValiditiesSection
                        mode="edit"
                        ruc={(step1Defaults.ruc as string | null | undefined) ?? proveedor?.ruc}
                        persons={persons}
                        // Mezcla los registros reales con los staged en
                        // memoria. Los pendientes llevan id "pending-…" y
                        // un documentFile stub (originalFileName del File
                        // local) para que el usuario vea el archivo cargado
                        // antes de hacer Siguiente.
                        powerValidities={[
                          ...pendingPowerValidities.map<PowerValidityResponse>(p => ({
                            id: `pending-${p.localId}`,
                            companyBusinessId: '',
                            companyPersonId: persons.find(x => x.contactType === 1)?.id ?? '',
                            personFullName: persons.find(x => x.contactType === 1)?.fullName ?? null,
                            code: p.code,
                            issueDate: p.issueDate,
                            expirationDate: '',
                            isValid: true,
                            documentFile: {
                              id: 'pending',
                              originalFileName: p.file.name,
                              storagePath: '',
                              contentType: p.file.type,
                              extension: null,
                              fileSize: p.file.size,
                              uploadedAt: new Date().toISOString(),
                            },
                            createdAt: new Date().toISOString(),
                          })),
                          ...powerValidities,
                        ]}
                        onAdd={async (cpId, data, file) => {
                          // Sin Company persistida → stage en memoria; el
                          // saveStepIdentity lo enviará en el FormData. Con
                          // Company → upload normal.
                          if (!effectiveCompanyId) {
                            setPendingPowerValidities(prev => [
                              ...prev,
                              { localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, code: data.code, issueDate: data.issueDate, file },
                            ])
                            return
                          }
                          const r = await proveedoresService.addPowerValidity(effectiveCompanyId, cpId, data, file)
                          setPowerValidities(prev => [r, ...prev])
                        }}
                        onRemove={async (id) => {
                          if (id.startsWith('pending-')) {
                            const localId = id.substring('pending-'.length)
                            setPendingPowerValidities(prev => prev.filter(p => p.localId !== localId))
                            return
                          }
                          if (!effectiveCompanyId) return
                          await proveedoresService.removePowerValidity(effectiveCompanyId, id)
                          setPowerValidities(prev => prev.filter(r => r.id !== id))
                        }}
                      />
                    )}
                  </>
                )}

                {activeTab === 'acreditacion' && (
                  <Step2Accreditation
                    ref={activeStepRef}
                    mode="edit"
                    defaultCategoryId={step2CategoryId}
                  />
                )}

                {activeTab === 'contrato' && (
                  <Step2bContrato
                    mode="edit"
                    activeContract={proveedor?.activeContract ?? null}
                    contractHistory={proveedor?.contractHistory ?? []}
                    onUpload={handleUploadContract}
                  />
                )}

                {activeTab === 'derechos' && (
                  <Step3MiningRights
                    mode="edit" companyId={effectiveCompanyId} savedMiningRights={savedMiningRights}
                    onSavePolygon={handleSavePolygon}
                    onAddComponent={handleAddComponent} onRemoveComponent={handleRemoveComponent}
                    onRefresh={refreshMiningRights}
                  />
                )}

                {activeTab === 'igafom' && (
                  <Step4Igafom
                    mode="edit" companyId={effectiveCompanyId} savedMiningRights={savedMiningRights}
                    minerSource={minerSource}
                    onAdd={async (mrId, data, receiptFile, documentFile) => { await proveedoresService.addIgafomRecord(effectiveCompanyId!, mrId, data, receiptFile, documentFile); await refreshMiningRights() }}
                    onUpdate={async (mrId, igId, data, receiptFile, documentFile) => { await proveedoresService.updateIgafomRecord(effectiveCompanyId!, mrId, igId, data, receiptFile, documentFile); await refreshMiningRights() }}
                    onRemove={async (mrId, igId) => { await proveedoresService.removeIgafomRecord(effectiveCompanyId!, mrId, igId); await refreshMiningRights() }}
                  />
                )}

                {activeTab === 'legaldocs' && (
                  <Step4bLegalDocuments
                    mode="edit" companyId={effectiveCompanyId} savedMiningRights={savedMiningRights}
                    onAdd={async (mrId, data, receiptFile, documentFile) => { await proveedoresService.addLegalDocumentRecord(effectiveCompanyId!, mrId, data, receiptFile, documentFile); await refreshMiningRights() }}
                    onUpdate={async (mrId, ldId, data, receiptFile, documentFile) => { await proveedoresService.updateLegalDocumentRecord(effectiveCompanyId!, mrId, ldId, data, receiptFile, documentFile); await refreshMiningRights() }}
                    onRemove={async (mrId, ldId) => { await proveedoresService.removeLegalDocumentRecord(effectiveCompanyId!, mrId, ldId); await refreshMiningRights() }}
                  />
                )}

                {activeTab === 'produccion' && (
                  <Step5Production
                    mode="edit" companyId={effectiveCompanyId} savedMiningRights={savedMiningRights}
                    onAdd={async (mrId, data, docFile) => { await proveedoresService.addProductionDeclaration(effectiveCompanyId!, mrId, data, docFile); await refreshMiningRights() }}
                    onRemove={async (mrId, declId) => { await proveedoresService.removeProductionDeclaration(effectiveCompanyId!, mrId, declId); await refreshMiningRights() }}
                  />
                )}

                {activeTab === 'recpo' && (
                  <Step6Recpo mode="edit" recpoRecords={recpoRecords} />
                )}

                {activeTab === 'iqbf' && (
                  <Step7Iqbf
                    mode="edit" companyId={effectiveCompanyId} savedMiningRights={savedMiningRights}
                    onToggleUsesExplosives={async (mrId, value) => { await proveedoresService.setMiningRightUsesExplosives(effectiveCompanyId!, mrId, value); await refreshMiningRights() }}
                    onAddAuthorization={async (mrId, data, file) => { await proveedoresService.addExplosiveAuthorization(effectiveCompanyId!, mrId, data, file); await refreshMiningRights() }}
                    onUpdateAuthorization={async (mrId, authId, data, file) => { await proveedoresService.updateExplosiveAuthorization(effectiveCompanyId!, mrId, authId, data, file); await refreshMiningRights() }}
                    onRemoveAuthorization={async (mrId, authId) => { await proveedoresService.removeExplosiveAuthorization(effectiveCompanyId!, mrId, authId); await refreshMiningRights() }}
                    onAddHandler={async (mrId, data) => { await proveedoresService.addExplosiveHandler(effectiveCompanyId!, mrId, data); await refreshMiningRights() }}
                    onBulkAddHandlers={async (mrId, items) => { await proveedoresService.bulkAddExplosiveHandlers(effectiveCompanyId!, mrId, items); await refreshMiningRights() }}
                    onRemoveHandler={async (mrId, handlerId) => { await proveedoresService.removeExplosiveHandler(effectiveCompanyId!, mrId, handlerId); await refreshMiningRights() }}
                  />
                )}

                {activeTab === 'finanzas' && (
                  <Step8Finance
                    ref={activeStepRef}
                    mode="edit" bankAccounts={bankAccounts}
                    onAddAccount={async (data) => { const r = await proveedoresService.addBankAccount(effectiveCompanyId!, data); setBankAccounts(prev => [...prev, r]); return r }}
                    onRemoveAccount={async (accountId) => { await proveedoresService.removeBankAccount(effectiveCompanyId!, accountId); setBankAccounts(prev => prev.filter(a => a.id !== accountId)) }}
                    incomeTaxCategoryId={incomeTaxCategoryId}
                    onIncomeTaxChange={setIncomeTaxCategoryId}
                    defaultSplaftDeclared={splaftDeclared}
                    onSplaftFileChange={setSplaftFile}
                    splaftDocumentFile={splaftDocumentFile}
                    taxReports={taxReports}
                    onAddTaxReport={async (data, file) => {
                      const r = await proveedoresService.addTaxReport(effectiveCompanyId!, data, file)
                      setTaxReports(prev => [r, ...prev])
                    }}
                    onRemoveTaxReport={async (reportId) => {
                      await proveedoresService.removeTaxReport(effectiveCompanyId!, reportId)
                      setTaxReports(prev => prev.filter(r => r.id !== reportId))
                    }}
                  />
                )}

                {activeTab === 'accionistas' && (
                  <Step10Ubo
                    shareholders={proveedor?.shareholders ?? []}
                    requiresEnhancedDueDiligence={proveedor?.requiresEnhancedDueDiligence ?? false}
                    onAddFromCertificate={async (file: File, pct: number) => {
                      if (!effectiveCompanyId) { toaster.error({ description: t('proveedores.wizard.toasts.supplierNotFound') }); return }
                      await proveedoresService.addShareholderFromCertificate(effectiveCompanyId, file, pct)
                      await refreshProveedor()
                    }}
                    onUpdate={async (shId: string, data: AddShareholderRequest, file?: File) => {
                      await proveedoresService.updateShareholder(effectiveCompanyId!, shId, data, file)
                      await refreshProveedor()
                    }}
                    onRemove={async (shId: string) => {
                      await proveedoresService.removeShareholder(effectiveCompanyId!, shId)
                      await refreshProveedor()
                    }}
                  />
                )}

                {activeTab === 'verificaciones' && (
                  <Step9Verification companyId={id} />
                )}

              {/* Actions Footer */}
              <Flex
                position="sticky" bottom={0} zIndex={10}
                bg={c.bgCard} px={8} py={4}
                justifyContent="space-between" alignItems="center"
                borderTop="1px solid" borderColor={c.border}
                mx={-6} mt="auto" pt={6}
              >
                <Box
                  as="button" type="button" display="flex" alignItems="center" gap={2}
                  color={c.accent} fontWeight="700" fontSize="sm" px={4} py={2}
                  borderRadius="md" cursor="pointer" _hover={{ bg: c.bgHover }}
                  onClick={() => (activeTab === 'consentimiento' || (activeTab === 'identidad' && isEditing)) ? navigate('/app/proveedores') : handleBack()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><polyline points="12 19 5 12 12 5"/></svg>
                  {(activeTab === 'consentimiento' || (activeTab === 'identidad' && isEditing)) ? t('proveedores.wizard.actions.cancel') : t('proveedores.wizard.actions.back')}
                </Box>
                <Flex gap={4}>
                  {isFullyRegistered && (
                    <Box
                      as="button" type="button" bg={c.bgCard} color={c.accent}
                      border="1px solid" borderColor={c.border} px={8} py={3} borderRadius="md"
                      fontWeight="700" fontSize="sm" cursor="pointer"
                      _hover={{ bg: c.bgHover }}
                      onClick={handleSaveAndExit}
                    >
                      {t('proveedores.wizard.actions.saveAndExit')}
                    </Box>
                  )}
                  <Box
                    as="button" type="submit" bg={c.accent} color={c.bgCard}
                    px={12} py={3} borderRadius="md" fontWeight="700" fontSize="sm"
                    cursor={isSaving ? 'not-allowed' : 'pointer'}
                    opacity={isSaving ? 0.6 : 1}
                    _hover={{ shadow: 'lg' }}
                    display="flex" alignItems="center" gap={2}
                    disabled={isSaving}
                  >
                    {activeTab === TAB_ORDER[TAB_ORDER.length - 1] ? (isFullyRegistered ? t('proveedores.wizard.actions.save') : t('proveedores.wizard.actions.saveAndExit')) : t('proveedores.wizard.actions.next')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><polyline points="12 5 19 12 12 19"/></svg>
                  </Box>
                </Flex>
              </Flex>
          </form>
        </Box>
      </Box>
    </Flex>
  )
}
